<template>
  <main class="index-page stats-page">
    <header class="page-header">
      <h1>数据统计</h1>
      <p>查看今日实时请求统计和历史日统计。</p>
    </header>

    <el-alert
      v-if="today && today.enabled === false"
      title="统计功能未开启，请在 config.yaml 中启用 stats.enabled"
      type="warning"
      show-icon
      :closable="false"
    />

    <section class="stats-grid" v-loading="loading">
      <el-card class="stat-card" shadow="never">
        <span>今日总请求</span>
        <strong>{{ formatNumber(today.total_requests) }}</strong>
      </el-card>
      <el-card class="stat-card" shadow="never">
        <span>今日有效请求</span>
        <strong>{{ formatNumber(today.success_requests) }}</strong>
      </el-card>
      <el-card class="stat-card" shadow="never">
        <span>今日失败请求</span>
        <strong>{{ formatNumber(today.failed_requests) }}</strong>
      </el-card>
      <el-card class="stat-card" shadow="never">
        <span>今日累计 Token</span>
        <strong>{{ formatNumber(today.total_tokens) }}</strong>
        <small>上游返回的 total_tokens</small>
      </el-card>
      <el-card class="stat-card" shadow="never">
        <span>今日累计扣费</span>
        <strong>{{ formatNumber(today.total_cost) }}</strong>
        <small>按 model_prices 倍率计算的 cost</small>
      </el-card>
      <el-card class="stat-card" shadow="never">
        <span>平均耗时</span>
        <strong>{{ formatLatency(today.avg_latency_ms) }}</strong>
      </el-card>
    </section>

    <section class="detail-grid">
      <el-card class="panel-card" shadow="never">
        <template #header>
          <div class="card-header">
            <span>今日渠道统计</span>
          </div>
        </template>
        <el-table :data="channelRows" empty-text="暂无渠道统计" stripe>
          <el-table-column prop="name" label="渠道" min-width="160" />
          <el-table-column prop="requests" label="请求数" width="110" />
          <el-table-column prop="errors" label="异常数" width="110" />
          <el-table-column label="失败率" width="110">
            <template #default="{ row }">{{ formatPercent(row.error_rate) }}</template>
          </el-table-column>
        </el-table>
      </el-card>

      <el-card class="panel-card" shadow="never">
        <template #header>
          <div class="card-header">
            <span>今日模型消耗</span>
          </div>
        </template>
        <el-table :data="modelRows" empty-text="暂无模型统计" stripe>
          <el-table-column prop="name" label="模型" min-width="180" />
          <el-table-column prop="tokens" label="Token" width="120" />
          <el-table-column prop="cost" label="扣费" width="120" />
        </el-table>
      </el-card>
    </section>

    <el-card class="panel-card" shadow="never">
      <template #header>
        <div class="card-header">
          <span>历史日统计</span>
          <div class="header-actions">
            <el-button type="danger" plain :disabled="loading || !selectedDailyIds.length" @click="removeSelectedDaily">
              删除已选
            </el-button>
            <el-button type="primary" plain :loading="loading" @click="loadStats">刷新</el-button>
          </div>
        </div>
      </template>
      <el-table
        :data="dailyItems"
        empty-text="暂无历史统计"
        stripe
        @selection-change="selectedDailyIds = $event.map((item) => item.id)"
      >
        <el-table-column type="selection" width="48" />
        <el-table-column prop="stat_date" label="日期" width="130" />
        <el-table-column prop="total_requests" label="总请求" width="110" />
        <el-table-column prop="success_requests" label="有效请求" width="110" />
        <el-table-column prop="failed_requests" label="失败请求" width="110" />
        <el-table-column prop="total_tokens" label="Token" width="130" />
        <el-table-column prop="total_cost" label="扣费" width="120" />
        <el-table-column label="平均耗时" width="120">
          <template #default="{ row }">{{ formatLatency(row.avg_latency_ms) }}</template>
        </el-table-column>
        <el-table-column label="渠道异常" min-width="220">
          <template #default="{ row }">
            <div v-if="mapEntries(row.channel_errors).length" class="map-tags">
              <el-tag
                v-for="entry in mapEntries(row.channel_errors)"
                :key="entry.name"
                type="danger"
                effect="plain"
                round
              >
                {{ entry.name }}：{{ entry.count }}
              </el-tag>
            </div>
            <span v-else>-</span>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-bar">
        <el-pagination
          v-model:current-page="dailyPage"
          v-model:page-size="dailyPageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="dailyTotal"
          layout="total, sizes, prev, pager, next, jumper"
          background
          @size-change="handleDailyPageSizeChange"
          @current-change="loadDailyStats"
        />
      </div>
    </el-card>
  </main>
</template>

<script>
import { deleteDailyStats, fetchDailyStats, fetchTodayStats } from '@/api/stats'
import './stats.css'

export default {
  name: 'StatsView',
  data() {
    return {
      loading: false,
      today: {},
      dailyItems: [],
      selectedDailyIds: [],
      dailyPage: 1,
      dailyPageSize: 20,
      dailyTotal: 0
    }
  },
  computed: {
    channelRows() {
      const requests = this.today.channel_requests || {}
      const errors = this.today.channel_errors || {}
      return Object.keys(requests)
        .map((name) => {
          const count = Number(requests[name] || 0)
          const failed = Number(errors[name] || 0)
          return {
            name,
            requests: count,
            errors: failed,
            error_rate: count > 0 ? failed / count : 0
          }
        })
        .sort((a, b) => b.requests - a.requests)
    },
    modelRows() {
      const tokens = this.today.model_tokens || {}
      const costs = this.today.model_costs || {}
      return Object.keys(tokens)
        .map((name) => ({
          name,
          tokens: Number(tokens[name] || 0),
          cost: Number(costs[name] || 0)
        }))
        .sort((a, b) => b.tokens - a.tokens)
    }
  },
  mounted() {
    this.loadStats()
  },
  methods: {
    async loadStats() {
      this.loading = true
      try {
        const [today, daily] = await Promise.all([fetchTodayStats(), this.fetchDaily()])
        this.today = today || {}
        this.applyDailyResult(daily)
      } finally {
        this.loading = false
      }
    },
    async loadDailyStats() {
      this.loading = true
      try {
        const daily = await this.fetchDaily()
        this.applyDailyResult(daily)
      } finally {
        this.loading = false
      }
    },
    fetchDaily() {
      return fetchDailyStats({
        page: this.dailyPage,
        page_size: this.dailyPageSize
      })
    },
    applyDailyResult(data = {}) {
      this.dailyItems = data.items || []
      this.dailyTotal = Number(data.total || 0)
      this.dailyPage = Number(data.page || this.dailyPage || 1)
      this.dailyPageSize = Number(data.page_size || this.dailyPageSize || 20)
      const visibleIds = this.dailyItems.map((item) => item.id)
      this.selectedDailyIds = this.selectedDailyIds.filter((id) => visibleIds.includes(id))
    },
    handleDailyPageSizeChange() {
      this.dailyPage = 1
      this.loadDailyStats()
    },
    async removeSelectedDaily() {
      if (!this.selectedDailyIds.length) {
        return
      }
      const confirmed = window.confirm(`确定删除选中的 ${this.selectedDailyIds.length} 条历史统计吗？`)
      if (!confirmed) {
        return
      }
      this.loading = true
      try {
        const deletedCount = this.selectedDailyIds.length
        await deleteDailyStats(this.selectedDailyIds)
        this.selectedDailyIds = []
        const maxPage = Math.max(1, Math.ceil((this.dailyTotal - deletedCount) / this.dailyPageSize))
        if (this.dailyPage > maxPage) {
          this.dailyPage = maxPage
        }
        await this.loadDailyStats()
      } finally {
        this.loading = false
      }
    },
    formatNumber(value) {
      const number = Number(value || 0)
      return Number.isFinite(number) ? number.toLocaleString('zh-CN') : '0'
    },
    formatLatency(value) {
      const ms = Number(value || 0)
      if (ms >= 1000) {
        return `${(ms / 1000).toFixed(2)} s`
      }
      return `${ms.toFixed(0)} ms`
    },
    formatPercent(value) {
      return `${(Number(value || 0) * 100).toFixed(2)}%`
    },
    mapEntries(value) {
      return Object.entries(value || {})
        .map(([name, count]) => ({
          name,
          count: Number(count || 0)
        }))
        .filter((item) => item.count > 0)
        .sort((a, b) => b.count - a.count)
    }
  }
}
</script>
